import { StepItem } from '@/types';
import { YesOrNo } from '@remodeling/base';
import {
  BalconyCoatingConstructionType,
  BathroomConstructionScopeType,
  BathroomType,
  CeilingDesignConstructionType,
  CeilingMoldingConstructionType,
  CentralDoorForType,
  CentralDoorTypeForType,
  ConstructionScope,
  DesiredDay,
  DoorConstructionType,
  EtcConstructionType,
  ExpansionConstructionAnswer,
  FilmConstructionType,
  FirstScopeType,
  FloorConstructionType,
  FloorLinoleumOption,
  FloorLivingRoomConstructionOption,
  FloorLivingRoomOption,
  FloorTileOption,
  Grade,
  InstallSmartSwitchYN,
  IsInstallPartition,
  KitchenAdditionalElectronicType,
  KitchenElectronicPackageType,
  KitchenOptionType,
  KitchenPlanType,
  LightingSpaceType,
  LightingType,
  PanelBathroomOption,
  SecondScopeType,
  SpaceStatus,
  StorageBuiltInConstructionType,
  StorageEntranceConstructionType,
  StorageFurnitureType,
  StyleType,
  TileBathroomOption,
  TileConstructionTileType,
  TileConstructionType,
  WallDesignConstructionType,
  WallpaperConstructionType,
  WallStyleConstructionType,
  WindowOptionType,
  WindowTypeForType,
} from '@remodeling/remodeling-manager';
import {
  GTM_UNTACT_BASE_CARPENTCEILING_EXIT,
  GTM_UNTACT_BASE_CARPENTWALL_EXIT,
  GTM_UNTACT_BASE_ETC_EXIT,
  GTM_UNTACT_BASE_EXPANSION_EXIT,
  GTM_UNTACT_BASE_FILM_EXIT,
  GTM_UNTACT_BASE_MOLDING_EXIT,
  GTM_UNTACT_BASE_PAINT_EXIT,
  GTM_UNTACT_BASE_TILE_EXIT,
  GTM_UNTACT_BASE_WALLPAPER_EXIT,
  GTM_UNTACT_BASICINFO_EXIT,
  GTM_UNTACT_PLAN_DAY_EXIT,
  GTM_UNTACT_PLAN_GRADE_EXIT,
  GTM_UNTACT_PLAN_SCOPE_EXIT,
  GTM_UNTACT_PLAN_SCRAP_EXIT,
  GTM_UNTACT_PLAN_STATUS_EXIT,
  GTM_UNTACT_PLAN_STYLE_EXIT,
  GTM_UNTACT_STYLE_BATH_EXIT,
  GTM_UNTACT_STYLE_CETRALDOOR_EXIT,
  GTM_UNTACT_STYLE_COMMENT_EXIT,
  GTM_UNTACT_STYLE_DOOR_EXIT,
  GTM_UNTACT_STYLE_FLOOR_EXIT,
  GTM_UNTACT_STYLE_KITCHEN_EXIT,
  GTM_UNTACT_STYLE_LIGHTING_EXIT,
  GTM_UNTACT_STYLE_STORAGE_EXIT,
  GTM_UNTACT_STYLE_WALLSTYLE_EXIT,
  GTM_UNTACT_STYLE_WINDOW_EXIT,
} from './gtm';

export type GradeType = 'BASIC' | 'DELUXE' | 'PREMIUM';
type NameValueType<T> = {
  name: string;
  value: T;
  desc?: string;
  grade?: GradeType;
};
type ArrayType<T> = NameValueType<T>[];

export const DEFAULT_STEP_LIST: StepItem[] = [
  {
    seq: 1,
    title: '기본정보',
    type: 'consult',
    gtm: {
      exitEvent: GTM_UNTACT_BASICINFO_EXIT,
    },
    subSteps: [],
  },
  {
    seq: 2,
    title: '공사계획',
    type: 'constructionPlan',
    subSteps: [
      {
        seq: 1,
        title: '공사범위',
        type: 'constructionScope',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        required: true,
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_SCOPE_EXIT,
        },
      },
      {
        seq: 2,
        title: '공사 희망일',
        type: 'desiredDay',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        required: true,
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_DAY_EXIT,
        },
      },
      {
        seq: 3,
        title: '현재 공간상태',
        type: 'spaceStatus',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        required: true,
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_STATUS_EXIT,
        },
      },
      {
        seq: 4,
        title: '선호 스타일',
        type: 'styles',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        required: true,
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_STYLE_EXIT,
        },
      },
      {
        seq: 5,
        title: '그레이드/예산',
        type: 'grade',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        required: true,
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_GRADE_EXIT,
        },
      },
      {
        seq: 6,
        title: '스크랩 공유',
        type: 'scraps',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        gtm: {
          exitEvent: GTM_UNTACT_PLAN_SCRAP_EXIT,
        },
      },
    ],
  },
  {
    seq: 3,
    title: '기본공사',
    type: 'baseConstruction',
    hiddenBySubStep: true,
    subSteps: [
      {
        seq: 1,
        title: '확장공사',
        type: 'expansionConstructions',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_EXPANSION_EXIT,
        },
      },
      {
        seq: 2,
        title: '목공공사-천장몰딩',
        type: 'carpentryConstruction.ceilingMoldingConstruction',
        relationPartialScope: 'MOLDING',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_MOLDING_EXIT,
        },
      },
      {
        seq: 3,
        title: '목공공사-천장디자인',
        type: 'carpentryConstruction.ceilingDesignConstructions',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_CARPENTCEILING_EXIT,
        },
      },
      {
        seq: 4,
        title: '목공공사-벽디자인',
        type: 'carpentryConstruction.wallDesignConstructions',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_CARPENTWALL_EXIT,
        },
      },
      {
        seq: 5,
        title: '타일공사',
        type: 'tileConstructions',
        relationPartialScope: 'TILE',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_TILE_EXIT,
        },
      },
      {
        seq: 6,
        title: '필름공사',
        type: 'filmConstructions',
        relationPartialScope: 'FILM',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_FILM_EXIT,
        },
      },
      {
        seq: 7,
        title: '도장-발코니',
        type: 'balconyCoatingConstruction',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_PAINT_EXIT,
        },
      },
      {
        seq: 8,
        title: '도배공사',
        type: 'wallpaperConstruction',
        relationPartialScope: 'WALLPAPER',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_WALLPAPER_EXIT,
        },
      },
      {
        seq: 9,
        title: '기타공사',
        type: 'etcConstructions',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_BASE_ETC_EXIT,
        },
      },
    ],
  },
  {
    seq: 4,
    title: '스타일공사',
    type: 'styleConstruction',
    hiddenBySubStep: true,
    subSteps: [
      {
        seq: 1,
        title: '창호',
        type: 'windowConstruction',
        relationPartialScope: 'WINDOW',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_WINDOW_EXIT,
        },
      },
      {
        seq: 2,
        title: '도어',
        type: 'doorConstruction',
        relationPartialScope: 'DOOR',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_DOOR_EXIT,
        },
      },
      {
        seq: 3,
        title: '중문',
        type: 'centralDoorConstruction',
        relationPartialScope: 'CENTRAL_DOOR',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_CETRALDOOR_EXIT,
        },
      },
      {
        seq: 4,
        title: '욕실',
        type: 'bathroomConstruction',
        relationPartialScope: 'BATHROOM',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_BATH_EXIT,
        },
      },
      {
        seq: 5,
        title: '바닥',
        type: 'floorConstructions',
        relationPartialScope: 'FLOOR',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_FLOOR_EXIT,
        },
      },
      {
        seq: 6,
        title: '부엌가구',
        type: 'kitchenFurnitureConstruction',
        relationPartialScope: 'KITCHEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_KITCHEN_EXIT,
        },
      },
      {
        seq: 7,
        title: '수납',
        type: 'storageFurnitureConstruction',
        relationPartialScope: 'STORAGE',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_STORAGE_EXIT,
        },
      },
      {
        seq: 8,
        title: '벽스타일',
        type: 'wallStyleConstructions',
        relationPartialScope: 'PARTIAL_RELATION_HIDDEN',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_WALLSTYLE_EXIT,
        },
      },
      {
        seq: 9,
        title: '조명',
        type: 'lightingConstruction',
        relationPartialScope: 'LIGHT',
        required: true,
        checkExclude: true,
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_LIGHTING_EXIT,
        },
      },
      {
        seq: 10,
        title: '남기고 싶은 말',
        type: 'comment',
        relationPartialScope: 'PARTIAL_RELATION_VISIBLE',
        gtm: {
          exitEvent: GTM_UNTACT_STYLE_COMMENT_EXIT,
        },
      },
    ],
  },
];

// 공사계획
/// 공사범위
export const CONSTRUCTION_SCOPES: ArrayType<ConstructionScope> = [
  { name: '전체공사', value: 'ALL' },
  { name: '부분공사', value: 'PARTIAL' },
];
export const FIRST_SCOPES: ArrayType<FirstScopeType> = [
  { name: '부엌', value: 'KITCHEN' },
  { name: '욕실', value: 'BATHROOM' },
];
export const SECOND_SCOPES: ArrayType<SecondScopeType> = [
  { name: '수납', value: 'STORAGE' },
  { name: '창호', value: 'WINDOW' },
  { name: '바닥', value: 'FLOOR' },
  { name: '중문', value: 'CENTRAL_DOOR' },
  { name: '도어', value: 'DOOR' },
  { name: '몰딩', value: 'MOLDING' },
  { name: '벽지', value: 'WALLPAPER' },
  { name: '조명', value: 'LIGHT' },
  { name: '타일', value: 'TILE' },
  { name: '필름', value: 'FILM' },
];

/// 공사 희망일
export const DESIRED_DAY_LIST: ArrayType<DesiredDay> = [
  { name: '1개월 이내', value: 1 },
  { name: '2개월 이내', value: 2 },
  { name: '3개월 이내', value: 3 },
  { name: '3개월 이후', value: 4 },
  { name: '공사일정 미정', value: 5 },
];

/// 현재 공간상태
export const SPACE_STATUS_LIST: ArrayType<SpaceStatus> = [
  { name: '공실', value: 'EMPTY' },
  { name: '본인 거주중', value: 'ME' },
  { name: '타인 거주중', desc: '이사예정', value: 'SOMEONE' },
];

/// 선호 스타일
export const STYLES_LIST: ArrayType<StyleType> = [
  { name: '퓨어 모던', value: 'PURE_MODERN' },
  {
    name: '어반 센서티비티',
    value: 'URBAN_SENSITIVITY',
  },
  { name: '내추럴 하모니', value: 'NATURAL_HARMONY' },
  { name: '오리엔탈 무드', value: 'ORIENTAL_MOOD' },
  {
    name: '유니크 미드센추리',
    value: 'UNIQUE_MID_CENTURY',
  },
  { name: '로맨틱 클래식', value: 'ROMANTIC_CLASSIC' },
];
export const STYLES_MIN_COUNT = 1;
export const STYLES_MAX_COUNT = 2;

/// 공사 그레이드
export const GRADE_LIST: ArrayType<Grade> = [
  {
    name: '베이직',
    desc: '중저가 상품들의 구성으로 군더더기 없이 깔끔한 스타일을 합리적 가격으로 제안해 드립니다.',
    value: 'BASIC',
  },
  {
    name: '디럭스',
    desc: '디자인과 기능적으로 포인트가 될만한 요소를 선택하여 효과적인 고급 상품의 조합을 제안해 드립니다.',
    value: 'DELUXE',
  },
  {
    name: '프리미엄',
    desc: '최신 스타일 트렌드를 반영하여 최고급 상품 및 공법을 전반에 활용한 고품격 인테리어를 제안 드립니다.',
    value: 'PREMIUM',
  },
];

// 기본공사
/// 확장공사
export const EXPANSION_CONSTRUCTION_LIST: ArrayType<ExpansionConstructionAnswer> =
  [
    { name: '예', value: 'Y' },
    { name: '아니오', value: 'N' },
    { name: '알 수 없음', value: 'UNKNOWN' },
  ];

/// 목공공사-천장몰딩
export const CEILING_MOLDING_CONSTRUCTION_LIST: ArrayType<CeilingMoldingConstructionType> =
  [
    {
      name: '공사제외',
      value: 'EXCLUDE',
    },
    {
      name: '히든몰딩',
      value: 'HIDDEN',
    },
    {
      name: '평몰딩',
      value: 'PY',
    },
    {
      name: '계단 몰딩',
      value: 'STAIR',
    },
    {
      name: '크라운 몰딩',
      value: 'CROWN',
    },
    {
      name: '마이너스 몰딩',
      value: 'MINUS',
    },
  ];

/// 목공공사 - 천장 디자인
export const CEILING_DESIGN_CONSTRUCTION_LIST: ArrayType<CeilingDesignConstructionType> =
  [
    { name: '공사제외', value: 'EXCLUDE' },
    {
      name: '우물천장',
      value: 'WOOMUL',
    },
    {
      name: '간접조명 우물천장',
      value: 'INDIRECT_WOOMUL',
    },
    {
      name: '일자 간접조명',
      value: 'INDIRECT_LIGHT',
    },
    {
      name: '라인 조명',
      value: 'LINE_LIGHT',
    },
    {
      name: '천장 평탄화',
      value: 'FLAT',
    },
  ];

/// 목공공사 - 벽디자인
export const WALL_DESIGN_CONSTRUCTION_LIST: ArrayType<WallDesignConstructionType> =
  [
    {
      name: '공사제외',
      value: 'EXCLUDE',
    },
    {
      name: '히든 걸레받이',
      value: 'HIDDEN',
    },
    {
      name: '아치형게이트',
      value: 'ARCH',
    },
    {
      name: '하프파티션',
      value: 'HALF',
    },
  ];
/// 타일공사
export const TILE_CONSTRUCTION_LIST: ArrayType<TileConstructionType> = [
  { name: '공사제외', value: 'EXCLUDE' },
  { name: '주방 벽', value: 'KITCHEN_WALL' },
  { name: '현관 바닥', value: 'ENTRANCE_FLOOR' },
  { name: '발코니 바닥', value: 'BALCONY_FLOOR' },
];

export const TILE_TYPE: ArrayType<TileConstructionTileType> = [
  { name: '일반타일', value: 'NORMAL' },
  { name: '대형타일', value: 'BIG' },
];

/// 필름공사
export const FILM_CONSTRUCTION_LIST: ArrayType<FilmConstructionType> = [
  { name: '공사제외', value: 'EXCLUDE' },
  { name: '현관 방화문(내측1면)', value: 'ENTRANCE' },
  { name: '중문', value: 'CENTRAL_DOOR' },
  { name: '천장 몰딩', value: 'CEILING_MOLDING' },
  { name: '걸레받이', value: 'BASEBOARD' },
  { name: '도어 전체', value: 'ALL_DOOR' },
  { name: '창호 전체', value: 'ALL_WINDOW' },
];
/// 도장 - 발코니
export const BALCONY_COATING_CONSTRUCTION_LIST: ArrayType<BalconyCoatingConstructionType> =
  [
    { name: '공사제외', value: 'EXCLUDE' },
    { name: '탄성코트', value: 'FLEXIBLE' },
    { name: '세라믹코트', value: 'CERAMIC' },
  ];

/// 도배공사
export const WALLPAPER_CONSTRUCTION_LIST: ArrayType<WallpaperConstructionType> =
  [
    { name: '공사제외', value: 'EXCLUDE' },
    { name: '실크 벽지 전체 시공', value: 'SILK' },
  ];

/// 기타공사
export const ETC_CONSTURCTION_LIST: ArrayType<EtcConstructionType> = [
  { name: '공사제외', value: 'EXCLUDE' },
  { name: '디지털 도어락', value: 'DIGITAL_DOOR_LOCK' },
  {
    name: '발코니 빨래건조대 (천장형)',
    value: 'BALCONY_LAUNDRY',
  },
  { name: '시스템 에어컨', value: 'SYSTEM_AIR' },
];

// 스타일공사
/// 창호
export const WINDOW_CONSTRUCTIONS: ArrayType<WindowTypeForType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: '일반창호 교체',
    value: 'NORMAL',
  },
  {
    name: '하이가드 방충망',
    value: 'NORMAL_HIGHGUARD',
  },
];

export const WINDOW_CONSTRUCTION_OPTIONS: ArrayType<WindowOptionType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: '터닝도어',
    value: 'TURNING',
  },
  {
    name: '폴딩도어',
    value: 'FOLDING',
  },
];
/// 도어 전체 교체 -> 도어 옵션
export const DOOR_CONSTRUCTION_TYPES: ArrayType<
  Exclude<DoorConstructionType, 'EXCLUDE'>
> = [
  {
    name: '일반도어',
    desc: '기본도어',
    value: 'NORMAL',
  },
  {
    name: '9mm 문선도어',
    desc: '문의 테두리를 매우 얇게 처리하여 깔끔한 디자인의 형태로 도어를 시공 합니다.',
    value: 'NINE_MM_LINE',
  },
  {
    name: '히든도어',
    desc: '벽면과 일직선을 이루며 도어가 마치 벽의 일부처럼 시공되는 상품 입니다.',
    value: 'HIDDEN',
  },
];

/// 중문
export const CENTRAL_DOORS: ArrayType<CentralDoorForType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: '스윙 중문',
    value: 'SWING',
  },
  {
    name: '3연동 중문',
    value: 'THREE_SLIDING',
  },
  {
    name: '프리미엄 스윙',
    value: 'PREMIUM_SWING',
  },
  {
    name: '프리미엄 슬라이딩',
    value: 'PREMIUM_SLIDING',
  },
  {
    name: '젠 슬라이딩',
    value: 'ZEN_SLIDING',
  },
  {
    name: '센서자동 슬라이딩',
    value: 'SENSOR_SLIDING',
  },
];
export const CENTRAL_DOOR_TYPES: ArrayType<CentralDoorTypeForType> = [
  {
    name: '고시형',
    value: 'SAFETY',
    desc: '중문 프레임 하단부를 높임으로써 안전과 위생을 중점으로 두었습니다.',
  },
  {
    name: '오픈형',
    value: 'OPEN',
    desc: '프레임 하단부를 낮추고 유리공간을 넓혀 확 트힌 개방감을 주는 디자인 입니다.',
  },
];
export const CENTRAL_PARTITION_ANSWERS: ArrayType<IsInstallPartition> = [
  {
    name: '공사제외',
    value: 'N',
  },
  {
    name: '파티션 사용',
    value: 'Y',
  },
];

/// 욕실
export const BATHROOM_CONSTRUCTION_SCOPES: ArrayType<BathroomConstructionScopeType> =
  [
    {
      name: '공사제외',
      value: 'EXCLUDE',
    },
    {
      name: '모든 욕실 교체',
      value: 'ALL',
    },
    {
      name: '일부 욕실 교체',
      value: 'PARTIAL',
    },
  ];
export const BATHROOM_TYPES: ArrayType<BathroomType> = [
  {
    name: '패널욕실',
    value: 'PANEL',
  },
  {
    name: '타일욕실',
    value: 'TILE',
  },
];
export const PANEL_BATHROOM_OPTIONS: ArrayType<PanelBathroomOption> = [
  {
    name: 'C시리즈 패널',
    value: 'PANEL_C_SERIES',
    desc: '패널 사이에 심플한 몰딩을 시공하여 이음매가 최소화 되어 청소 및 관리가 용이한 상품 입니다.',
  },
  {
    name: 'S시리즈 패널',
    value: 'PANEL_S_SERIES',
    desc: '무몰딩 클릭 결합 구조로 마치 하나의 패널로 보여져 고급스러운 디자인 연출이 가능한 상품 입니다.',
  },
];
export const TILE_BATHROOM_OPTIONS: ArrayType<TileBathroomOption> = [
  {
    name: '일반타일',
    value: 'TILE_NORMAL',
  },
  {
    name: '대형타일',
    value: 'TILE_BIG',
  },
];

/// 바닥
export const FLOOR_CONSTRUCTIONS: ArrayType<FloorConstructionType> = [
  { name: '공사제외', value: 'EXCLUDE' },
  {
    name: '타일',
    value: 'TILE',
  },
  {
    name: '장판',
    value: 'LINOLEUM',
  },
  {
    name: '마루',
    value: 'LIVING_ROOM',
  },
];
/// 바닥 공사 > 타일 > 타일옵션
export const FLOOR_TILE_TYPES: ArrayType<FloorTileOption> = [
  {
    name: '일반타일',
    value: 'TILE_NORMAL',
  },
  {
    name: '대형타일',
    value: 'TILE_BIG',
  },
];
/// 바닥공사 -> 장판 -> 장판두꼐
export const FLOOR_LINOLEUM_OPTIONS: ArrayType<FloorLinoleumOption> = [
  {
    name: '두께 2.2T (mm)',
    value: 'LINOLEUM_THICKNESS_22T',
  },
  {
    name: '두께 3.5T (mm)',
    value: 'LINOLEUM_THICKNESS_35T',
  },
  {
    name: '두께 4.5T (mm)',
    value: 'LINOLEUM_THICKNESS_45T',
  },
];
/// 바닥공사 -> 마루 -> 마루종류
export const FLOOR_LIVING_ROOM_OPTIONS: ArrayType<FloorLivingRoomOption> = [
  {
    name: '원목마루',
    value: 'LIVING_ROOM_WOOD',
    desc: '최고급 천연목재를 활용한 프리미엄 마루 입니다. 원목 본연의 아름다움으로 웅장한 느낌을 연출 합니다.',
  },
  {
    name: '브러쉬마루',
    value: 'LIVING_ROOM_BRUSH',
    desc: '천연질감을 살린 무늬목으로 고급스러움을  연출하고 열전도성이 우수하여 온돌마루라고도 불립니다.',
  },
  {
    name: '강마루',
    value: 'LIVING_ROOM_KANG_MARU',
    desc: '표면강도가 높고 열전도율이 우수해 소비자가 가장 많이 찾는 마루 입니다. 컬러 및 패턴이 다양 합니다.',
  },
];
/// 바닥공사 -> 마루 -> 강마루, 브러시마루 -> 마루시공방식
export const FLOOR_LIVING_ROOM_CONSTRUCTION_OPTIONS: ArrayType<FloorLivingRoomConstructionOption> =
  [
    {
      name: '선형',
      value: 'LINEAR',
    },
    {
      name: '헤링본',
      value: 'HERRINGBONE',
    },
  ];

/// 부엌형태
export const KITCHEN_PLANS: ArrayType<KitchenPlanType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: 'ㅡ자',
    value: 'A_TYPE',
  },
  {
    name: 'ㄱ자',
    value: 'B_TYPE',
  },
  {
    name: 'ㄷ자',
    value: 'C_TYPE',
  },
];
/// 부엌 옵션가구
export const KITCHEN_OPTIONS: ArrayType<KitchenOptionType> = [
  {
    name: '제외',
    value: 'EXCLUDE',
  },
  {
    name: '카운터',
    value: 'COUNTER',
  },
  {
    name: '아일랜드',
    value: 'IRELAND',
  },
  {
    name: '가전소물키큰장',
    value: 'ELECTRONIC_TALL',
  },
  {
    name: '냉장고장',
    value: 'REFRIGERATOR',
  },
  {
    name: '키큰장',
    value: 'TALL',
  },
];
export const KITCHEN_OPTION_MAX_COUNT = 5;
/// 4대 상품기기 선택
export const KITCHEN_ELECTRONIC_PACKAGES: ArrayType<KitchenElectronicPackageType> =
  [
    {
      name: '일반형',
      value: 'NORMAL',
    },
    {
      name: '고급형',
      value: 'ADVANCED',
    },
    {
      name: '럭셔리형',
      value: 'LUXURY',
    },
  ];
/// 추가 옵션기기
export const KITCHEN_ADDITIONAL_ELECTRONICS: ArrayType<KitchenAdditionalElectronicType> =
  [
    {
      name: '제외',
      value: 'EXCLUDE',
    },
    {
      name: '빌트인 냉장고',
      value: 'BUILTIN_REFRIGERATOR',
    },
    {
      name: '오븐',
      value: 'OVEN',
    },
    {
      name: '식기 세척기',
      value: 'DISH_WASHER',
    },
    {
      name: '빌트인 정수기',
      value: 'BUILTIN_WATER_PURIFIER',
    },
    {
      name: '매립형 콘센트',
      value: 'BUILTIN_OUTLET',
    },
  ];
/// 수납공사 항목 리스트
export const STORAGE_FURNITURE_TYPES: ArrayType<StorageFurnitureType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: '현관장',
    value: 'ENTRANCE',
  },
  {
    name: '붙박이장',
    value: 'BUILT_IN',
  },
  {
    name: '반침장',
    value: 'HALF',
  },
];
/// 수납공사 - 현관장객체
export const STORAGE_ENTRANCE_CONSTRUCTIONS: ArrayType<StorageEntranceConstructionType> =
  [
    {
      name: '일반형',
      value: 'NORMAL',
      desc: '가구 하부가 깔끔하게 마감되어 외부로 노출되는 부분은 최소화 한 현관장 입니다.',
    },
    {
      name: '행잉형',
      value: 'HANGING',
      desc: '가구 하단의 공간을 띄움 시공 하여 자주 사용되는 신발을 보관하기에 용이한 현관장 입니다.',
    },
    {
      name: '장식장형',
      value: 'DECORATED',
      desc: '가구 중앙 부분을 소품 장식 및 거울을 활용할 수 있는 장식장 형태의 현관장 입니다.',
    },
    {
      name: '벤치형',
      value: 'BENCH',
      desc: '가구 중앙에 벤치 형태로 설계되어 신발을 신고 벗기 용이한 공간으로 제공하는 현관장 입니다.',
    },
  ];
export const STORAGE_ENTRANCE_UNDER_LIGHT_ANSWERS: ArrayType<YesOrNo> = [
  {
    name: '공사제외',
    value: 'N',
  },
  {
    name: '설치',
    value: 'Y',
  },
];
/// 수납공사 - 붙박이장 객체
export const STORAGE_BUILT_IN_BANNER: ArrayType<any> = [
  {
    name: '스윙도어',
    value: 'SWING',
  },
  {
    name: '슬라이딩도어',
    value: 'SLIDING',
  },
  {
    name: '파우더장',
    value: 'POWDER',
  },
];

/// 수납공사 - 붙박이장 객체
export const STORAGE_BUILT_IN_CONSTRUCTIONS: ArrayType<StorageBuiltInConstructionType> =
  [
    {
      name: '스윙도어',
      value: 'SWING',
    },
    {
      name: '슬라이딩도어',
      value: 'SLIDING',
    },
    {
      name: '스윙도어 + 파우더장',
      value: 'SWING_POWDER',
    },
    {
      name: '슬라이딩도어 + 파우더장',
      value: 'SLIDING_POWDER',
    },
  ];
export const WALL_STYLE_CONSTRUCTIONS: ArrayType<WallStyleConstructionType> = [
  {
    name: '공사제외',
    value: 'EXCLUDE',
  },
  {
    name: '키친판넬',
    value: 'KITCHEN_PANEL',
    desc: '주방 벽에 타일 대신 이음매가 적고 일체감 높은 대형패널로 시공해 관리가 쉽고 자석활용으로 유용 합니다.',
  },
  {
    name: '월플러스',
    value: 'WALLPLUS',
    desc: '타일, 원목 또는 대리석에 비해 합리적 가격으로 포인트가 되는 아트월을 연출 할 수 있는 시공 상품 입니다.',
  },
  {
    name: '웨인스코팅',
    value: 'WAINS_COATING',
    desc: '클래식한 인테리어 연출을 위해 프레임 형태로 벽을 디자인 하는 시공 상품 입니다.',
  },
  {
    name: '미가루버',
    value: 'RUBBER',
    desc: '길고 가는 판을 일정한 간격으로 수직 배열하여 디자인 된 벽 포인트 상품 입니다.',
  },
];
/// 조명공간
export const LIGHTING_SPACE_TYPES: ArrayType<LightingSpaceType> = [
  { name: '공사제외', value: 'EXCLUDE' },
  {
    name: '거실',
    value: 'LIVING',
  },
  {
    name: '주방',
    value: 'KITCHEN',
  },
  {
    name: '방',
    value: 'ROOM',
  },
];
/// 조명 종류
export const LIGHTING_TYPES: ArrayType<LightingType> = [
  {
    name: '메인등',
    value: 'MAIN',
  },
  {
    name: '레일등',
    value: 'RAIL',
  },
  {
    name: '매립등',
    value: 'LANDFILL',
  },
];
/// IOT 스마트홈 조명 스위치
export const IS_INSTALL_SMART_SWITCHES: ArrayType<InstallSmartSwitchYN> = [
  {
    name: '일반 스위치 시공',
    value: 'N',
  },
  {
    name: '스마트홈 스위치 시공',
    value: 'Y',
  },
];
